import React from 'react';
import {useTranslation} from 'react-i18next';
import {
    buildPortalSurfaceRows,
    PORTAL_SURFACE,
    relativeTimeLabel,
} from '../../portal_usage/portalUsageState';

// Qué es cada superficie, en el idioma del producto. `REPORT_VIEW` no está: esas filas llevan el
// nombre del reporte que abrió el cliente, que es el dato accionable ("abrió Rendimiento mensual
// 18 veces" sirve; "abrió reportes 28 veces" no).
const SURFACE_LABELS = {
    [PORTAL_SURFACE.DASHBOARD]: {key: 'portal_usage.surface_dashboard', fallback: 'Tablero'},
    [PORTAL_SURFACE.MEMORY]: {key: 'portal_usage.surface_memory', fallback: 'Memoria del cliente'},
    [PORTAL_SURFACE.CONNECTIONS]: {key: 'portal_usage.surface_connections', fallback: 'Conexiones'},
};

/**
 * Qué abrió el cliente dentro del portal web, aparte de lo que le preguntó al asistente.
 *
 * Son dos señales distintas y por eso se cuentan por separado: un cliente que entra todos los días
 * a mirar su tablero pero nunca conectó su IA está adoptando el portal, no el asistente — y la
 * conversación comercial con cada uno de los dos es otra.
 */
export function PortalNavigationList({portalWeb}) {
    const {t} = useTranslation();
    const rows = buildPortalSurfaceRows(portalWeb);
    if (!rows.length) return null;

    return (
        <div className="box pu-box">
            <div className="pu-box__head">
                <h3 className="pu-box__title">{t('portal_usage.navigation_title', 'Qué abrió en el portal')}</h3>
            </div>
            <p className="pu-box__sub">
                {t('portal_usage.navigation_sub',
                    'La navegación dentro del portal web, aparte de lo que le pregunta al asistente.')}
            </p>

            <table className="pu-table">
                <thead>
                    <tr>
                        <th>{t('portal_usage.navigation_col_section', 'Sección')}</th>
                        <th className="-num">{t('portal_usage.navigation_col_views', 'Veces')}</th>
                        <th className="-num">{t('portal_usage.navigation_col_days', 'Días')}</th>
                        <th>{t('portal_usage.navigation_col_last', 'Última vez')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => (
                        <tr key={row.key}>
                            <td>
                                <span className={`pu-kind${row.surface === PORTAL_SURFACE.REPORT_VIEW ? ' -report' : ''}`}>
                                    {row.surface === PORTAL_SURFACE.REPORT_VIEW
                                        ? t('portal_usage.surface_report', 'Reporte')
                                        : t('portal_usage.surface_section', 'Sección')}
                                </span>
                                {/* El nombre del reporte lo escribió la agencia: va como children,
                                    sin interpolar en una key de i18n. */}
                                {row.name || labelForSurface(row.surface, t)}
                            </td>
                            <td className="-num">{row.views}</td>
                            {/* El desglose por reporte no trae días distintos: el guion dice que el
                                dato no viaja, no que sea cero. */}
                            <td className="-num">{row.activeDays === null ? '—' : row.activeDays}</td>
                            <td className="-muted">{relativeTimeLabel(row.lastAt, t) || '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function labelForSurface(surface, t) {
    const label = SURFACE_LABELS[surface];
    // Una superficie nueva del backend se muestra con su código antes que desaparecer de la tabla:
    // una fila que falta se lee como "no lo usó".
    return label ? t(label.key, label.fallback) : surface;
}

export default PortalNavigationList;
